import { useCallback, useMemo } from 'react';
import { useStore } from 'stores/Store';
import {
    ANALYTICS_EVENTS,
    VENDOR_VIEW_SOURCES,
    RENEWAL_FREQUENCIES,
    getProjectedRenewalDate,
    toVendorId,
    identifyUser,
    trackEvent,
} from 'lib/analytics';

/**
 * Component-facing analytics hook. Wraps trackEvent so every event
 * automatically carries the current user_id / username when available
 * (matches the Pearl Planner reference where the hook attaches user
 * context once instead of forcing each call site to remember).
 */
const useAnalytics = () => {
    const { authUser } = useStore();

    const userContext = useMemo(
        () => ({
            user_id: authUser?.Id ?? undefined,
            user_username: authUser?.Username ?? undefined,
            is_authed: !!authUser?.isAuthed,
        }),
        [authUser?.Id, authUser?.Username, authUser?.isAuthed]
    );

    const track = useCallback(
        (eventName, properties = {}) => {
            trackEvent(eventName, { ...userContext, ...properties });
        },
        [userContext]
    );

    const identify = useCallback((userId, traits = {}) => {
        identifyUser(userId, traits);
    }, []);

    return {
        track,
        identify,
        events: ANALYTICS_EVENTS,
        vendorViewSources: VENDOR_VIEW_SOURCES,
        renewalFrequencies: RENEWAL_FREQUENCIES,
        getProjectedRenewalDate,
        toVendorId,
    };
};

export { useAnalytics };
